// BuildingPlanOverlays2D.tsx — Buildings Set1 §6.3: the 2D PLAN overlays for a non-prototype
// `Mod_Building` (wall outline rings, door/window markers, roof cluster outline, active-floor
// gating). Rendered INSIDE `MapObjectUI`'s per-object group (the 2D branch of `MapSurface`), so it
// inherits the object's world position + rotation transform that `MapObjectUI` already applies —
// this component draws in the building's LOCAL frame. P2 implements the plan view; the full 3D
// geometry lives in `Building3D` (mounted from `MapObject3D` in the 3D branch).
import React from "react";
import * as THREE from "three";
import {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {GetObjDescendants} from "../../../../../Server/DB/mapObjects/$mapObject.js";
import {
	ResolveBuildingFloors, ResolveRoomFootprint_InBuilding,
	ResolveBuildingGeometryPlan, ResolveRoomWallRings,
} from "../../../../../Server/DB/mapObjects/BuildingGeometryAccessors.js";
import {FindOwningBuilding, FindOwningLevel, GetEnabledMod, ResolveBuildingMaterials} from "../../../../../Server/DB/mapObjects/BuildingAccessors.js";
import {Mod_Room} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_Room.js";
import type {ResolvedRoomMaterials} from "../../../../../Server/DB/mapObjects/Building/BuildingGeometry.js";
import {SurfaceMaterialManager} from "../../../../../Building/Materials/SurfaceMaterialManager.js";
import {mapZIndexes} from "../MapZIndexes.js";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import type {MapState} from "../../../../../Store/main/maps/@MapState.js";

const WALL_LINE_COLOR = "#8a7a6a";
const WALL_FILL_COLOR = "#8a7a6a";
const SWING_VIZ_COLOR = "#2a5a2a";
const DOOR_OR_WINDOW_HOLE_BOUNDS_COLOR = "#e0c040";
const OPENING_HOLE_BOUNDS_COLOR = "#ff5500";
const ROOF_OUTLINE_COLOR = "#5a6ea0";

/** The 2D plan overlay set for a non-prototype Building, drawn in the building's local frame.
 *  Set2 §9: the active floor's MERGED WALL BAND (+ opening markers) is drawn ONCE by
 *  `FloorWallsOverlay2D` — a shared wall is one band, no double line, no z-fighting — and
 *  `RoomOverlay2D` is thinned to the room's floor finish + faint reference lines. Mounted from
 *  `MapObjectUI` (2D mode) only. */
export const BuildingPlanOverlays2D = observer_mgl_r3((props: {obj: MapObject, mapState: MapState})=>{
	const {obj, mapState} = props;
	const floorsRes = ResolveBuildingFloors(obj);
	const activeFloor = mapState.buildings.activeFloor;

	// Gather the rooms that sit on the active floor, plus the resolved FLOOR plan (merged walls +
	// floor-level openings) that the wall band is drawn from.
	const roomsOnFloor = CollectRoomsOnFloor(obj, activeFloor, floorsRes.floors);
	const floorPlan = ResolveBuildingGeometryPlan(obj)?.floors.find(f=>f.floorNumber == activeFloor) ?? null;

	return (
		<>
			<FloorWallsOverlay2D floorPlan={floorPlan}/>
			{roomsOnFloor.map(roomObj=>(
				<RoomOverlay2D key={roomObj.id} obj={obj} roomObj={roomObj} floorPlan={floorPlan}/>
			))}
			{/* Roof cluster outline on the topmost floor */}
			<RoofOutline2D obj={obj}/>
		</>
	);
});

/** Set2 §9: the active floor's MERGED wall band — one polyline pair per merged segment (its
 *  interior + exterior faces, from the merge engine's pre-mitered endpoints) plus that segment's
 *  openings. A shared wall is ONE merged segment, so it draws ONE band (no double line). The
 *  shared/exterior distinction is shown by the band-end caps' opacity: a shared (interior) wall
 *  gets faint caps so it reads differently from an exterior wall at a glance (§10 F4). */
const FloorWallsOverlay2D = observer_mgl_r3((props: {floorPlan: any})=>{
	const {floorPlan} = props;
	const walls = floorPlan?.walls;
	if (walls == null || walls.segments.length == 0) return null;

	// Openings grouped by floor-segment index.
	const openingsBySegment = new Map<number, any[]>();
	for (const group of floorPlan.openingsBySegment ?? []) openingsBySegment.set(group.segmentIndex, group.openings);

	return (
		<>
			{walls.segments.map((seg: any, i: number)=>{
				const innerFace: [number, number][] = [seg.innerStart, seg.innerEnd];
				const outerFace: [number, number][] = [seg.outerStart, seg.outerEnd];
				const openings = openingsBySegment.get(i) ?? [];
				const capOpacity = seg.shared ? 0.3 : 0.85;
				return (
					<React.Fragment key={i}>
						<Polyline points={innerFace} closed={false} color={WALL_LINE_COLOR} opacity={1}/>
						<Polyline points={outerFace} closed={false} color={WALL_LINE_COLOR} opacity={1}/>
						{/* end caps — the band's thickness at each end (fainter for a shared wall) */}
						<Polyline points={[seg.innerStart, seg.outerStart]} closed={false} color={WALL_LINE_COLOR} opacity={capOpacity}/>
						<Polyline points={[seg.innerEnd, seg.outerEnd]} closed={false} color={WALL_LINE_COLOR} opacity={capOpacity}/>
						{openings.map((op: any)=>(
							<FloorOpeningMarker2D key={op.objID} opening={op} segment={seg}/>
						))}
					</React.Fragment>
				);
			})}
		</>
	);
});

/** A Room's 2D plan overlay, thinned (Set2 §9): the room's floor MATERIAL fill plus faint
 *  reference lines. The wall band itself is drawn by `FloorWallsOverlay2D` (once per merged
 *  segment) so a shared wall is never double-drawn. */
const RoomOverlay2D = observer_mgl_r3((props: {obj: MapObject, roomObj: MapObject, floorPlan: any})=>{
	const {obj, roomObj, floorPlan} = props;

	// The merged plan carries the room's authored + resolved rings; fall back to the resolvers when
	// the room was filtered out of the merge (e.g. a degenerate t/2 inset).
	const faceLines = floorPlan?.walls?.wallsPerRoom?.find((w: any)=>w.roomObjID == roomObj.id) ?? null;
	const sourceRing: [number, number][]|null = faceLines?.sourceRing
		?? ResolveRoomFootprint_InBuilding(roomObj, obj)?.points
		?? null;
	const innerFace: [number, number][]|null = faceLines?.innerFace
		?? ResolveRoomWallRings(roomObj, obj)?.centerline
		?? null;
	const outerRing: [number, number][]|null = faceLines?.outer ?? null;

	return (
		<>
			{/* P6 — the room's FLOOR MATERIAL rendered over the interior (the "the floor material
			    renders" 2D deliverable, §6.3): a surface-mode procedural material filling the
			    room's footprint at its floorTopZ. */}
			<FloorFill2D obj={obj} roomObj={roomObj}/>
			{/* faint fill of the interior (the room's INNER FACE — flush at shared walls) */}
			{innerFace != null && innerFace.length >= 3 && (
				<Polyline points={innerFace} closed color={WALL_FILL_COLOR} opacity={0.3}/>
			)}
			{/* the authored outline (the wall CENTERLINE source, Set2 §2) as a reference line */}
			{sourceRing != null && sourceRing.length >= 3 && (
				<Polyline points={sourceRing} closed color={WALL_LINE_COLOR} opacity={0.35}/>
			)}
			{/* the exterior wall face, when the merge provided it (a degenerate-inset fallback skip) */}
			{outerRing != null && outerRing.length >= 3 && (
				<Polyline points={outerRing} closed color={WALL_LINE_COLOR} opacity={0.5}/>
			)}
		</>
	);
});

/** The door/window marker for a FLOOR-level bound opening (Set2 §9). Same swing-arc / doubled-line
 *  visualization as before, but positioned against the MERGED segment's INTERIOR face (the face the
 *  opening was bound to and the wall builder punches). The wall band itself is drawn by
 *  `FloorWallsOverlay2D`, so this draws only the opening + its hole bounds. */

const FloorOpeningMarker2D = observer_mgl_r3((props: {opening: any, segment: any})=>{
	const {opening, segment} = props;
	// Position against the segment's INTERIOR face — the face the opening was bound to and the wall
	// builder punches (`alongCenter` is metres along `innerLength`).
	const [sx, sy] = segment.innerStart;
	const [ex, ey] = segment.innerEnd;
	const len = segment.innerLength || 1;
	const t = opening.alongCenter / len;
	const cx = sx + (ex - sx) * t;
	const cy = sy + (ey - sy) * t;
	const halfW = opening.width / 2;
	const ax = (ex - sx) / len, ay = (ey - sy) / len; // unit along-axis (segment start→end)
	// Unit OUTWARD normal — the merged segment's own normal.
	const nx = segment.normal[0], ny = segment.normal[1];
	const wallThickness = segment.wallThickness;

	const p1: [number, number] = [cx - ax * halfW, cy - ay * halfW];
	const p2: [number, number] = [cx + ax * halfW, cy + ay * halfW];
	// Exterior-face corners: the hole spans the full wall thickness outward along the normal.
	const p1o: [number, number] = [p1[0] + nx * wallThickness, p1[1] + ny * wallThickness];
	const p2o: [number, number] = [p2[0] + nx * wallThickness, p2[1] + ny * wallThickness];
	const holeBounds: [number, number][] = [p1, p2, p2o, p1o];

	if (opening.kind == "opening") {
		// A full-height wall opening: just the punched hole bounds + a single center line (no
		// sill / frame / swing arc). This is the "open space between two Rooms" marker.
		return (
			<>
				<Polyline points={holeBounds} closed color={OPENING_HOLE_BOUNDS_COLOR} opacity={0.9}/>
				<Polyline points={[p1, p2]} closed={false} color={SWING_VIZ_COLOR} opacity={1}/>
			</>
		);
	}
	if (opening.kind == "door") {
		// Quarter-arc swing anchored at the door's HINGE corner of the hole rect, sweeping from the
		// closed-slab edge toward the swing side. Left/Right = hinge at −along/+along (p1/p2);
		// in/out = swing toward the interior face (p1/p2) or exterior face (p1o/p2o).
		const swing = opening.model?.door?.swing ?? "inRight";
		const hingeOnNegAlong = swing.endsWith("Left"); // hinge at p1 (−along) vs p2 (+along)
		const swingsIntoRoom = swing.startsWith("in");  // swing toward interior (−normal) vs exterior (+normal)
		// The hinge corner of the hole rect (interior face for `in*`, exterior face for `out*`).
		const hinge: [number, number] = swingsIntoRoom
			? (hingeOnNegAlong ? p1 : p2)
			: (hingeOnNegAlong ? p1o : p2o);
		// Closed-slab direction away from the hinge (along the wall), and the swing direction
		// perpendicular to it (into the room or into the exterior).
		const slabDir: [number, number] = hingeOnNegAlong ? [ax, ay] : [-ax, -ay];
		const swingDir: [number, number] = swingsIntoRoom ? [-nx, -ny] : [nx, ny];
		// Sweep slabDir → swingDir (a quarter turn): point(φ) = H + r·(slabDir·cos φ + swingDir·sin φ).
		const arcPoints: [number, number][] = [];
		const radius = opening.width;
		const steps = 8;
		for (let i = 0; i <= steps; i++) {
			const ang = (i / steps) * (Math.PI / 2);
			const dx = slabDir[0] * Math.cos(ang) + swingDir[0] * Math.sin(ang);
			const dy = slabDir[1] * Math.cos(ang) + swingDir[1] * Math.sin(ang);
			arcPoints.push([hinge[0] + dx * radius, hinge[1] + dy * radius]);
		}
		return (
			<>
				<Polyline points={holeBounds} closed color={DOOR_OR_WINDOW_HOLE_BOUNDS_COLOR} opacity={0.9}/>
				<Polyline points={[p1, p2]} closed={false} color={SWING_VIZ_COLOR} opacity={1}/>
				<Polyline points={arcPoints} closed={false} color={SWING_VIZ_COLOR} opacity={0.6}/>
			</>
		);
	}
	// Window: doubled line across the opening span.
	const q1: [number, number] = [p1[0] + nx * 0.1, p1[1] + ny * 0.1];
	const q2: [number, number] = [p2[0] + nx * 0.1, p2[1] + ny * 0.1];
	return (
		<>
			<Polyline points={holeBounds} closed color={DOOR_OR_WINDOW_HOLE_BOUNDS_COLOR} opacity={0.9}/>
			<Polyline points={[p1, p2]} closed={false} color={SWING_VIZ_COLOR} opacity={1}/>
			<Polyline points={[q1, q2]} closed={false} color={SWING_VIZ_COLOR} opacity={0.6}/>
		</>
	);
});

/** The roof cluster outline on the building's topmost floor, drawn as a dashed line. Set2 §9: the
 *  outline is now the floor's merged `centerlineUnion` (the union of every room's centerline
 *  footprint on that floor) instead of the per-room interior-face + outward offset — for
 *  axis-aligned rooms this is the same shaped footprint the roof was always built from. Falls back
 *  to the authored per-room outlines when the floor has no merged plan (e.g. the topmost floor's
 *  rooms were all degenerate). */
const RoofOutline2D = observer_mgl_r3((props: {obj: MapObject})=>{
	const {obj} = props;
	const floorsRes = ResolveBuildingFloors(obj);
	// Topmost claimed floor (max positive floor number).
	const numbers = [...floorsRes.floors.keys()].filter(n=>n >= 1);
	if (numbers.length == 0) return null;
	const topFloor = Math.max(...numbers);
	const roomsOnFloor = CollectRoomsOnFloor(obj, topFloor, floorsRes.floors);
	if (roomsOnFloor.length == 0) return null;

	const floorPlan = ResolveBuildingGeometryPlan(obj)?.floors.find(f=>f.floorNumber == topFloor) ?? null;
	const unionRing: [number, number][]|null = floorPlan?.walls?.centerlineUnion ?? null;
	const outlines = unionRing != null && unionRing.length >= 3
		? [unionRing]
		: roomsOnFloor.map(roomObj=>{
			const inBuilding = ResolveRoomFootprint_InBuilding(roomObj, obj);
			return inBuilding?.points ?? [];
		}).filter(p=>p.length >= 3);

	return (
		<>
			{outlines.map((pts, i)=>(
				<Polyline key={i} points={pts} closed color={ROOF_OUTLINE_COLOR} opacity={0.8}/>
			))}
		</>
	);
});

// ---------------------------------------------------------------------------
// P6 — 2D floor material fill
// ---------------------------------------------------------------------------
/** Resolve the 2D floor material for a room's footprint (a surface-mode `floorTop` recipe),
 *  returning a `{material, geo}` pair the `<primitive>`/`<mesh>` JSX can attach. Renders the
 *  room's floor material in PLAN view via a `ShapeGeometry` over the interior footprint (UVs in
 *  metres, so the recipe's scales are meaningful), with the `vSurfaceNormal`/`vSurfaceUV`
 *  attributes the surface-mode shader expects + per-frame frame uniforms.
 *
 *  Pure procedural: a `THREE.Mesh` whose material is the assembled `surfaceMode` fragment and
 *  whose geometry is the room footprint extruded flat at `floorTopZ`. The `uv` attribute is the
 *  room-local (metre) XY, giving the floor recipe its world-anchored texture coordinates. */
const FloorFill2D = observer_mgl_r3((props: {obj: MapObject, roomObj: MapObject})=>{
	const {obj, roomObj} = props;
	const inBuilding = ResolveRoomFootprint_InBuilding(roomObj, obj);
	if (inBuilding == null || inBuilding.points.length < 3) return null;

	const roomMaterials = ResolveBuildingMaterials(roomObj) as unknown as ResolvedRoomMaterials;
	const recipeID = roomMaterials.floorTop ?? "wood";
	const managerRef = React.useRef<SurfaceMaterialManager|null>(null);
	if (managerRef.current == null) managerRef.current = new SurfaceMaterialManager();
	const manager = managerRef.current;

	const [material, setMaterial] = React.useState<THREE.ShaderMaterial|null>(null);
	const geoRef = React.useRef<THREE.ShapeGeometry|null>(null);

	// Build the footprint ShapeGeometry once per footprint (UV = metre XY).
	const geo = React.useMemo(()=>{
		const pts = inBuilding.points as [number, number][];
		const shape = new THREE.Shape();
		shape.moveTo(pts[0][0], pts[0][1]);
		for (let i = 1; i < pts.length; i++) shape.lineTo(pts[i][0], pts[i][1]);
		shape.closePath();
		const g = new THREE.ShapeGeometry(shape);
		// The ShapeGeometry's UV = the shape's (x,y) coordinates already (metres) — ideal.
		geoRef.current = g;
		return g;
	}, [inBuilding]);

	// Resolve the surface material for the floor recipe on mount / recipe change.
	React.useEffect(()=>{
		let cancelled = false;
		(async ()=>{
			if (!recipeID) return;
			const entry = await manager.getSurfaceMaterial(obj.map, recipeID, {transparent: false});
			if (cancelled) return;
			const clone = manager.cloneSurfaceMaterial(entry);
			manager.setFrameUniforms(clone, {metersPerPixel: 0.05, surfaceElevation: 0});
			setMaterial(clone);
		})();
		return ()=>{ cancelled = true; };
	}, [recipeID, obj.map, manager]);

	// Dispose the geometry + material on unmount/change.
	React.useEffect(()=>{
		return ()=>{ geo.dispose(); if (geoRef.current) geoRef.current.dispose(); if (material) material.dispose(); };
	}, [geo, material]);

	if (material == null) return null;
	const z = mapZIndexes.objShape - 1; // just under the outlines
	return (
		<mesh geometry={geo} material={material} position={[0, 0, z]}/>
	);
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** The rooms of `buildingObj` that sit on `floorNumber`. A room's owning level (nearest ancestor
 *  `Mod_Level`) claims floor numbers; a room whose level claims `floorNumber` (or a room with no
 *  level, defaulting to floor 1) is on the floor. Rooms are the descendants of the building (not
 *  inside a nested non-prototype building) carrying an enabled `Mod_Room`. */
function CollectRoomsOnFloor(buildingObj: MapObject, floorNumber: number, _floors: Map<number, any>): MapObject[] {
	const rooms: MapObject[] = [];
	// Include the building object itself in the subtree: a Room can be co-located with the
	// Building (the common single-room case, §3.1), mirroring `CollectRoomsForLevel`.
	const subtree = [buildingObj, ...GetObjDescendants(buildingObj)];
	for (const desc of subtree) {
		const mod = GetEnabledMod(desc, "room") as Mod_Room|n;
		if (mod == null) continue;
		if (IsRoomOnFloor(desc, buildingObj, floorNumber)) rooms.push(desc);
	}
	return rooms;
}

/** True if `roomObj` is an enabled Room belonging to `buildingObj` that sits on `floorNumber`. */
function IsRoomOnFloor(roomObj: MapObject, buildingObj: MapObject, floorNumber: number): boolean {
	// Skip rooms inside a nested non-prototype building (it owns its own floors).
	const owningBuilding = FindOwningBuilding(roomObj);
	if (owningBuilding && owningBuilding.obj.id != buildingObj.id) return false;
	// Determine which floor this room sits on.
	const level = FindOwningLevel(roomObj);
	const levelNumbers = level ? (level.mod.floorNumbers ?? [1]).filter(n=>n != null) : [1];
	const numbers = levelNumbers.length ? levelNumbers : [1];
	return numbers.includes(floorNumber);
}

/** A polyline rendered at `mapZIndexes.objShape`. Uses `bufferGeometry` + `bufferAttribute`
 *  (the R3F line pattern from `BasicShape`); `closed` appends the first point at the end. */
function Polyline(props: {points: Array<[number, number]>, closed: boolean, color: string, opacity: number}) {
	const {points, closed, color, opacity} = props;
	const pts = closed && points.length ? [...points, points[0]] : points;
	const z = mapZIndexes.objShape;
	const positions = new Float32Array(pts.flatMap(([x, y])=>[x, y, z]));
	return (
		<line>
			<bufferGeometry>
				<bufferAttribute attach="attributes-position" args={[positions, 3]}/>
			</bufferGeometry>
			<lineBasicMaterial color={color} transparent={opacity < 1} opacity={opacity}/>
		</line>
	);
}
